import {
  AccessoryProductType,
  AirHandlerTypeCodeType,
  ApiMutationResponseType,
  AttomDataType,
  ConcealedDuctLocationType,
  CustomerStructureForDatabaseType,
  CustomerStructureType,
  CustomSystemType,
  ExistingSystemComponentType,
  LevelForDatabaseType,
  LineSetType,
  LiveChatDataType,
  ProjectAttachmentType,
  ProjectDetailsType,
  SectionForDatabaseType,
  SelectedSystemType,
  SpaceForDatabaseType,
  StructureImageMetadataType,
  StructureImageTransformType,
  StructureStepCompletedType,
  SystemSelectorInstanceType,
  UpsertCustomSystemRequestType,
  ZoneType
} from 'data/types';
import {
  AccessoriesRecommendationsRequestType,
  ManualAccessoryLogRequestType,
  QuotableSystemAccessoryRecommendationType,
  RequiredAccessoryGroupType,
  SavedQuotableSystemType
} from './accessories-api-types';
import { emptyApi } from './empty-api';

type ConfigurationForDatabaseInput = {
  uniqueId: string;
  instanceUniqueId?: string | null;
  label: string;
  valid: boolean;
  modified: boolean;
  tags?: string[];
};

type CondenserForDatabaseInput = {
  uniqueId: string;
  levelUniqueId: string;
  condenserTypeCode: 'ductless' | 'ducted' | 'hybrid';
  label: string;
  heightOffGround: number;
  noAccessoriesNeeded: boolean;
  modified: boolean;
  valid: boolean;
  positionX: number | null;
  positionY: number | null;
  placementType?: 'ground' | 'wall' | 'roof';
  accessories?: Array<{ productId: number; quantity: number }>;
};

type DuctRunPointInput = { uniqueId: string; x: number; y: number };

type AdditionalDuctRunInput = {
  uniqueId: string;
  x: number;
  y: number;
  points?: DuctRunPointInput[];
};

type ZoneForDatabaseInput = {
  uniqueId: string;
  condenserUniqueId?: string | null;
  label: string;
  zoneTypeCode: ZoneType['type'];
  existingSystemUniqueId?: string | null;
  valid: boolean;
  modified: boolean;
  spaceUniqueIds: string[];
  spaceItems?: Array<{
    structureItemId: string;
    registerOverride?: { x: number; y: number } | null;
    additionalSupplyRuns?: AdditionalDuctRunInput[];
    supplyRunPoints?: DuctRunPointInput[];
    servePassively?: boolean | null;
  }>;
  returnOverride?: { x: number; y: number } | null;
  additionalReturnRuns?: AdditionalDuctRunInput[];
  returnRunPoints?: DuctRunPointInput[];
};

type AirHandlerForDatabaseInput = {
  uniqueId: string;
  zoneUniqueId: string;
  airHandlerTypeCode: AirHandlerTypeCodeType;
  positionX: number;
  positionY: number;
  valid: boolean;
  modified: boolean;
  userPositioned?: boolean | null;
  concealedLocation?: ConcealedDuctLocationType | null;
};

const projectApi = emptyApi.injectEndpoints({
  endpoints: (builder) => ({
    getPropertyData: builder.query<AttomDataType, string>({
      query: (address) => `property-data?address=${address}`
    }),
    getLiveChatData: builder.query<LiveChatDataType, number>({
      query: (projectId) => `live-chat?projectId=${projectId}`
    }),
    getProjectDetails: builder.query<ProjectDetailsType, number>({
      query: (projectId) => `project-details?projectId=${projectId}`
    }),
    getInstance: builder.query<
      SystemSelectorInstanceType,
      { projectId: number; structureUniqueId?: string }
    >({
      query: ({ projectId, structureUniqueId }) =>
        structureUniqueId && structureUniqueId.toLowerCase() !== 'new'
          ? `instance?projectId=${projectId}&structureUniqueId=${structureUniqueId}`
          : `instance?projectId=${projectId}`
    }),
    getStructures: builder.query<CustomerStructureType[], number>({
      query: (projectId) => `structures?projectId=${projectId}`
    }),
    getProjectAttachments: builder.query<
      { images: ProjectAttachmentType[]; files: ProjectAttachmentType[] },
      number
    >({
      query: (projectId) => `project-attachments?projectId=${projectId}`
    }),
    saveQuoteData: builder.mutation<
      ApiMutationResponseType,
      { quoteId: number; quoteData: Partial<SelectedSystemType> }
    >({
      query: ({ quoteId, quoteData }) => ({
        url: 'quote-data',
        method: 'POST',
        body: { quoteId, quoteData: JSON.stringify(quoteData) }
      })
    }),
    saveInstance: builder.mutation<
      ApiMutationResponseType,
      Partial<SystemSelectorInstanceType>
    >({
      query: (instanceData) => ({
        url: 'instance',
        method: 'PATCH',
        body: instanceData
      })
    }),
    saveStructure: builder.mutation<
      ApiMutationResponseType,
      {
        projectId: number;
        addressId?: number;
        structure: CustomerStructureForDatabaseType;
      }
    >({
      query: ({ projectId, addressId, structure }) => ({
        url: 'structure',
        method: 'PATCH',
        body: {
          projectId,
          addressId,
          structure
        }
      })
    }),
    saveBaseStructure: builder.mutation<
      ApiMutationResponseType,
      {
        projectId: number;
        addressId?: number;
        structure: Omit<CustomerStructureForDatabaseType, 'levels'>;
      }
    >({
      query: ({ projectId, addressId, structure }) => ({
        url: 'base-structure',
        method: 'PATCH',
        body: {
          projectId,
          addressId,
          structure
        }
      })
    }),
    updateComponentReplacing: builder.mutation<
      { success: boolean },
      { uniqueId: string; replacing: boolean }
    >({
      query: ({ uniqueId, replacing }) => ({
        url: `existing-equipment/component/${uniqueId}/replacing`,
        method: 'PATCH',
        body: { replacing }
      })
    }),
    updateExistingEquipmentComponent: builder.mutation<
      { success: boolean },
      { component: ExistingSystemComponentType }
    >({
      query: ({ component }) => ({
        url: `existing-equipment/component/${component.uniqueId}`,
        method: 'PATCH',
        body: { component }
      })
    }),
    saveLevel: builder.mutation<
      { levelUniqueId: string },
      Omit<LevelForDatabaseType, 'uniqueId'> & { uniqueId?: string }
    >({
      query: (levelData) => ({
        url: 'structure/level',
        method: 'PATCH',
        body: levelData
      })
    }),
    saveLevels: builder.mutation<
      { levelUniqueIds: string[] },
      {
        levels: (Omit<LevelForDatabaseType, 'uniqueId'> & {
          uniqueId?: string;
        })[];
      }
    >({
      query: ({ levels }) => ({
        url: 'structure/levels',
        method: 'PATCH',
        body: { levels }
      })
    }),
    saveSection: builder.mutation<
      { sectionUniqueId: string },
      Omit<SectionForDatabaseType, 'uniqueId'> & { uniqueId?: string }
    >({
      query: (sectionData) => ({
        url: 'structure/section',
        method: 'PATCH',
        body: sectionData
      })
    }),
    saveSections: builder.mutation<
      { sectionUniqueIds: string[] },
      {
        sections: (Omit<SectionForDatabaseType, 'uniqueId'> & {
          uniqueId?: string;
        })[];
      }
    >({
      query: ({ sections }) => ({
        url: 'structure/sections',
        method: 'PATCH',
        body: { sections }
      })
    }),
    saveSpace: builder.mutation<
      { spaceUniqueId: string },
      Omit<SpaceForDatabaseType, 'uniqueId'> & { uniqueId?: string }
    >({
      query: (spaceData) => ({
        url: 'structure/space',
        method: 'PATCH',
        body: spaceData
      })
    }),
    saveSpaces: builder.mutation<
      { spaceUniqueIds: string[] },
      {
        spaces: (Omit<SpaceForDatabaseType, 'uniqueId'> & {
          uniqueId?: string;
        })[];
      }
    >({
      query: ({ spaces }) => ({
        url: 'structure/spaces',
        method: 'PATCH',
        body: { spaces }
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['SizingLoad'])
    }),
    deleteLevel: builder.mutation<
      { levelUniqueIds: string[]; deleted: boolean },
      { levelUniqueIds: string[]; structureUniqueId: string }
    >({
      query: (data) => ({
        url: 'structure/level',
        method: 'DELETE',
        body: data
      })
    }),
    deleteSection: builder.mutation<
      { sectionUniqueIds: string[]; deleted: boolean },
      { sectionUniqueIds: string[]; structureUniqueId: string }
    >({
      query: (data) => ({
        url: 'structure/section',
        method: 'DELETE',
        body: data
      })
    }),
    deleteSpace: builder.mutation<
      { spaceUniqueIds: string[]; deleted: boolean },
      { spaceUniqueIds: string[]; structureUniqueId: string }
    >({
      query: (data) => ({
        url: 'structure/space',
        method: 'DELETE',
        body: data
      })
    }),
    generateSizingItemsAndAttributes: builder.mutation<
      {
        walls: { created: number; updated: number; deleted: number };
        floors: { created: number; updated: number; deleted: number };
        ceilings: { created: number; updated: number; deleted: number };
        spaceAttributes: { updated: number };
        affectedSpaceUniqueIds: string[];
      },
      {
        structureUniqueId: string;
        generateSizingItems?: boolean;
        applySpaceAboveAndBelow?: boolean;
      }
    >({
      query: ({
        structureUniqueId,
        generateSizingItems,
        applySpaceAboveAndBelow
      }) => ({
        url: 'structure/generate-sizing-items-and-attributes',
        method: 'POST',
        body: {
          structureUniqueId,
          generateSizingItems,
          applySpaceAboveAndBelow
        }
      })
    }),
    generateWindows: builder.mutation<
      {
        created: number;
        updated: number;
        deleted: number;
        affectedSpaceUniqueIds: string[];
      },
      {
        structureUniqueId: string;
        width: number;
        height: number;
      }
    >({
      query: ({ structureUniqueId, width, height }) => ({
        url: 'structure/generate-windows',
        method: 'POST',
        body: { structureUniqueId, width, height }
      })
    }),
    updateStructureOrientation: builder.mutation<
      {
        updated: number;
        affectedSpaceUniqueIds: string[];
      },
      {
        structureUniqueId: string;
        oldOrientation: string;
        newOrientation: string;
      }
    >({
      query: ({ structureUniqueId, oldOrientation, newOrientation }) => ({
        url: 'structure/update-orientation',
        method: 'POST',
        body: { structureUniqueId, oldOrientation, newOrientation }
      })
    }),
    applySizingDefaultsRetroactively: builder.mutation<
      {
        itemsChecked: number;
        itemsUpdated: number;
        affectedSpaceUniqueIds: string[];
      },
      { structureUniqueId: string }
    >({
      query: ({ structureUniqueId }) => ({
        url: 'structure/apply-sizing-defaults-retroactively',
        method: 'POST',
        body: { structureUniqueId }
      })
    }),
    getStructureImageMetadata: builder.query<
      StructureImageMetadataType[],
      string
    >({
      query: (structureUniqueId) =>
        `structure-image-metadata?structureUniqueId=${structureUniqueId}`
    }),
    assignImageToStructureItem: builder.mutation<
      { success: boolean },
      {
        attachmentId: number;
        structureItemUniqueId: string;
        structureItemTypeCode?: 'level' | 'section' | 'space';
      }
    >({
      query: ({
        attachmentId,
        structureItemUniqueId,
        structureItemTypeCode = 'level'
      }) => ({
        url: 'assign-image-to-structure-item',
        method: 'POST',
        body: { attachmentId, structureItemUniqueId, structureItemTypeCode }
      })
    }),
    unassignImageFromStructureItem: builder.mutation<
      { success: boolean },
      { attachmentId: number }
    >({
      query: ({ attachmentId }) => ({
        url: 'unassign-image-from-structure-item',
        method: 'POST',
        body: { attachmentId }
      })
    }),
    updateStructureImageTransform: builder.mutation<
      { success: boolean },
      { attachmentId: number } & StructureImageTransformType
    >({
      query: ({
        attachmentId,
        scale,
        rotation,
        startPositionX,
        startPositionY
      }) => ({
        url: 'update-structure-image-transform',
        method: 'PATCH',
        body: { attachmentId, scale, rotation, startPositionX, startPositionY }
      })
    }),
    saveConfiguration: builder.mutation<
      { uniqueId: string },
      ConfigurationForDatabaseInput
    >({
      query: (data) => ({
        url: 'configuration',
        method: 'PATCH',
        body: data
      })
    }),
    saveConfigurationCondensers: builder.mutation<
      { condenserUniqueIds: string[] },
      { configurationUniqueId: string; condensers: CondenserForDatabaseInput[] }
    >({
      query: ({ configurationUniqueId, condensers }) => ({
        url: 'configuration/condensers',
        method: 'PATCH',
        body: { configurationUniqueId, condensers }
      })
    }),
    saveConfigurationZones: builder.mutation<
      { zoneUniqueIds: string[] },
      {
        configurationUniqueId: string;
        zones: ZoneForDatabaseInput[];
      }
    >({
      query: ({ configurationUniqueId, zones }) => ({
        url: 'configuration/zones',
        method: 'PATCH',
        body: { configurationUniqueId, zones }
      })
    }),
    saveConfigurationAirHandlers: builder.mutation<
      { airHandlerUniqueIds: string[] },
      {
        configurationUniqueId: string;
        airHandlers: AirHandlerForDatabaseInput[];
      }
    >({
      query: ({ configurationUniqueId, airHandlers }) => ({
        url: 'configuration/air-handlers',
        method: 'PATCH',
        body: { configurationUniqueId, airHandlers }
      })
    }),
    saveConfigurationLineSets: builder.mutation<
      { lineSetUniqueIds: string[] },
      { configurationUniqueId: string; lineSets: LineSetType[] }
    >({
      query: ({ configurationUniqueId, lineSets }) => ({
        url: 'configuration/line-sets',
        method: 'PATCH',
        body: { configurationUniqueId, lineSets }
      })
    }),
    deleteConfiguration: builder.mutation<void, string>({
      query: (uniqueId) => ({
        url: `configuration?uniqueId=${uniqueId}`,
        method: 'DELETE'
      })
    }),
    deleteConfigurationCondensers: builder.mutation<
      void,
      { condenserUniqueIds: string[] }
    >({
      query: ({ condenserUniqueIds }) => ({
        url: 'configuration/condensers',
        method: 'DELETE',
        body: { condenserUniqueIds }
      })
    }),
    deleteConfigurationZones: builder.mutation<
      void,
      { zoneUniqueIds: string[] }
    >({
      query: ({ zoneUniqueIds }) => ({
        url: 'configuration/zones',
        method: 'DELETE',
        body: { zoneUniqueIds }
      })
    }),
    deleteConfigurationAirHandlers: builder.mutation<
      void,
      { airHandlerUniqueIds: string[] }
    >({
      query: ({ airHandlerUniqueIds }) => ({
        url: 'configuration/air-handlers',
        method: 'DELETE',
        body: { airHandlerUniqueIds }
      })
    }),
    deleteConfigurationLineSets: builder.mutation<
      void,
      { lineSetUniqueIds: string[] }
    >({
      query: ({ lineSetUniqueIds }) => ({
        url: 'configuration/line-sets',
        method: 'DELETE',
        body: { lineSetUniqueIds }
      })
    }),
    getStructureStepsCompleted: builder.query<
      StructureStepCompletedType[],
      string
    >({
      query: (structureUniqueId) =>
        `structure-steps-completed?structureUniqueId=${structureUniqueId}`,
      providesTags: ['StructureStepsCompleted']
    }),
    saveStructureStepCompleted: builder.mutation<
      { success: boolean },
      | {
          structureUniqueId: string;
          stepCode: string;
          subStepCode: string | null;
          completed: boolean;
          configurationUniqueId?: string | null;
        }
      | Array<{
          structureUniqueId: string;
          stepCode: string;
          subStepCode: string | null;
          completed: boolean;
          configurationUniqueId?: string | null;
        }>
    >({
      query: (body) => ({
        url: 'structure-step-completed',
        method: 'POST',
        body
      }),
      invalidatesTags: ['StructureStepsCompleted']
    }),
    deleteStructureStepCompleted: builder.mutation<
      { success: boolean },
      | {
          structureUniqueId: string;
          stepCode: string;
          subStepCode: string | null;
          configurationUniqueId?: string | null;
        }
      | Array<{
          structureUniqueId: string;
          stepCode: string;
          subStepCode: string | null;
          configurationUniqueId?: string | null;
        }>
    >({
      query: (body) => ({
        url: 'structure-step-completed',
        method: 'DELETE',
        body
      }),
      invalidatesTags: ['StructureStepsCompleted']
    }),
    saveQuotableSystem: builder.mutation<
      { uniqueId: string },
      {
        uniqueId: string;
        configurationUniqueId: string;
        products: Array<{
          productTypeCode: 'condenser' | 'air_handler' | 'accessory';
          productId: number;
          quantity: number;
          configurationItemUniqueId?: string;
        }>;
      }
    >({
      query: (body) => ({
        url: 'quotable-system',
        method: 'PUT',
        body
      }),
      invalidatesTags: ['QuotableSystems', 'AccessoriesRecommendations']
    }),
    getCustomSystems: builder.query<
      { customSystems: CustomSystemType[] },
      string
    >({
      query: (configurationUniqueId) =>
        `configurations/${configurationUniqueId}/custom-systems`,
      providesTags: (_result, _err, configurationUniqueId) => [
        { type: 'CustomSystems', id: configurationUniqueId }
      ]
    }),
    upsertCustomSystem: builder.mutation<
      { uniqueId: string },
      UpsertCustomSystemRequestType
    >({
      query: (body) => ({
        url: 'custom-system',
        method: 'PUT',
        body
      }),
      invalidatesTags: (_result, _err, body) => [
        { type: 'CustomSystems', id: body.configurationUniqueId }
      ]
    }),
    deleteCustomSystem: builder.mutation<
      void,
      { uniqueId: string; configurationUniqueId: string }
    >({
      query: ({ uniqueId }) => ({
        url: `custom-system/${uniqueId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _err, body) => [
        { type: 'CustomSystems', id: body.configurationUniqueId }
      ]
    }),
    renameCustomSystem: builder.mutation<
      void,
      { uniqueId: string; configurationUniqueId: string; name: string }
    >({
      query: ({ uniqueId, name }) => ({
        url: `custom-system/${uniqueId}/name`,
        method: 'PATCH',
        body: { name }
      }),
      invalidatesTags: (_result, _err, body) => [
        { type: 'CustomSystems', id: body.configurationUniqueId }
      ]
    }),
    deleteQuotableSystem: builder.mutation<void, string>({
      query: (uniqueId) => ({
        url: `quotable-system/${uniqueId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['QuotableSystems', 'AccessoriesRecommendations']
    }),
    updateQuotableSystemAccessories: builder.mutation<
      void,
      {
        uniqueId: string;
        accessories: Array<{
          productId: number;
          quantity: number;
          configurationItemUniqueId?: string;
          productTypeCode:
            | 'condenser_accessory'
            | 'air_handler_accessory'
            | 'accessory';
        }>;
      }
    >({
      query: ({ uniqueId, ...body }) => ({
        url: `quotable-system/${uniqueId}/accessories`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['QuotableSystems']
    }),
    updateQuotableSystemAccessoriesReviewed: builder.mutation<
      void,
      {
        uniqueId: string;
        accessoriesReviewed: boolean;
      }
    >({
      query: ({ uniqueId, ...body }) => ({
        url: `quotable-system/${uniqueId}/accessories-reviewed`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['QuotableSystems']
    }),
    getQuotableSystemsAccessoriesRecommendations: builder.query<
      QuotableSystemAccessoryRecommendationType[],
      AccessoriesRecommendationsRequestType
    >({
      query: (body) => ({
        url: 'quotable-systems/accessories-recommendations',
        method: 'POST',
        body
      }),
      providesTags: ['AccessoriesRecommendations']
    }),
    getQuotableSystemRequiredAccessoryOptions: builder.query<
      RequiredAccessoryGroupType[],
      string
    >({
      query: (uniqueId) =>
        `quotable-system/${uniqueId}/required-accessory-options`
    }),
    getSystemAccessoryTypes: builder.query<
      Array<{
        code: string;
        label: string;
        priority: number;
        limitOnePerSystem: boolean;
        description: string;
      }>,
      void
    >({
      query: () => 'system-accessory-types'
    }),
    getQuotableSystems: builder.query<SavedQuotableSystemType[], string[]>({
      query: (uniqueIds) => ({
        url: 'quotable-systems',
        method: 'POST',
        body: { uniqueIds }
      }),
      providesTags: ['QuotableSystems']
    }),
    searchAccessoryProducts: builder.query<AccessoryProductType[], string>({
      query: (term) =>
        `accessory-products/search?term=${encodeURIComponent(term)}`
    }),
    logManualAccessory: builder.mutation<
      { id: number },
      ManualAccessoryLogRequestType
    >({
      query: ({ uniqueId, ...body }) => ({
        url: `quotable-system/${uniqueId}/manual-accessory-log`,
        method: 'POST',
        body
      })
    }),
    getQuoteDataByInstance: builder.query<
      Array<{
        quoteId: string;
        quoteData: Partial<SelectedSystemType>;
        quotableSystemUniqueId: string | null;
      }>,
      { instanceUniqueId: string; structureUniqueId: string }
    >({
      query: ({ instanceUniqueId, structureUniqueId }) =>
        `quote-data-by-instance?instanceUniqueId=${instanceUniqueId}&structureUniqueId=${structureUniqueId}`,
      providesTags: ['QuoteData']
    }),
    getProductStockStatus: builder.query<
      { inStock: boolean; restockDate: string | null },
      number[]
    >({
      query: (productIds) =>
        `product-stock-status?productIds=${productIds.join(',')}`
    }),
    analyzeZones: builder.mutation<
      {
        role: string;
        content: string;
      },
      {
        systemName: string;
        condensers: Array<{
          condenserModel: string;
          condenserBtu: number;
          zones: Array<{
            zoneName: string;
            zoneId: string;
            spaces: string[];
            airHandlerModel: string;
            airHandlerBtu: number;
            requiredBtu: number;
            type: 'ductless' | 'ducted';
          }>;
        }>;
        heatingScore: number;
        coolingScore: number | null;
        coolingRequired: boolean;
        price: number;
      }
    >({
      query: (body) => ({
        url: 'ai/analyze-zones',
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useGetPropertyDataQuery,
  useGetLiveChatDataQuery,
  useGetProjectDetailsQuery,
  useGetInstanceQuery,
  useGetStructuresQuery,
  useGetProjectAttachmentsQuery,
  useSaveQuoteDataMutation,
  useSaveInstanceMutation,
  useSaveStructureMutation,
  useSaveBaseStructureMutation,
  useUpdateComponentReplacingMutation,
  useUpdateExistingEquipmentComponentMutation,
  useSaveLevelMutation,
  useSaveLevelsMutation,
  useSaveSectionMutation,
  useSaveSectionsMutation,
  useSaveSpaceMutation,
  useSaveSpacesMutation,
  useDeleteLevelMutation,
  useDeleteSectionMutation,
  useDeleteSpaceMutation,
  useGenerateSizingItemsAndAttributesMutation,
  useGenerateWindowsMutation,
  useUpdateStructureOrientationMutation,
  useApplySizingDefaultsRetroactivelyMutation,
  useGetStructureImageMetadataQuery,
  useAssignImageToStructureItemMutation,
  useUnassignImageFromStructureItemMutation,
  useUpdateStructureImageTransformMutation,
  useGetStructureStepsCompletedQuery,
  useSaveStructureStepCompletedMutation,
  useDeleteStructureStepCompletedMutation,
  useSaveConfigurationMutation,
  useSaveConfigurationCondensersMutation,
  useSaveConfigurationZonesMutation,
  useSaveConfigurationAirHandlersMutation,
  useSaveConfigurationLineSetsMutation,
  useDeleteConfigurationMutation,
  useDeleteConfigurationCondensersMutation,
  useDeleteConfigurationZonesMutation,
  useDeleteConfigurationAirHandlersMutation,
  useDeleteConfigurationLineSetsMutation,
  useSaveQuotableSystemMutation,
  useDeleteQuotableSystemMutation,
  useGetCustomSystemsQuery,
  useUpsertCustomSystemMutation,
  useDeleteCustomSystemMutation,
  useRenameCustomSystemMutation,
  useUpdateQuotableSystemAccessoriesMutation,
  useUpdateQuotableSystemAccessoriesReviewedMutation,
  useGetQuotableSystemsAccessoriesRecommendationsQuery,
  useGetQuotableSystemRequiredAccessoryOptionsQuery,
  useGetSystemAccessoryTypesQuery,
  useGetQuotableSystemsQuery,
  useLazyGetQuotableSystemsQuery,
  useSearchAccessoryProductsQuery,
  useLogManualAccessoryMutation,
  useGetQuoteDataByInstanceQuery,
  useGetProductStockStatusQuery,
  useAnalyzeZonesMutation
} = projectApi;
